import { Dialog, DialogActions, DialogContent, DialogTitle, Stack, Typography } from '@mui/material';
import { FunnelOverview } from 'features/co-pilot/campaign-overview/campaign-overview.types';
import PaiCheckbox from 'features/ui/base-components/pai-checkbox';
import PaiLink from 'features/ui/base-components/pai-link';
import PaiMenuItem from 'features/ui/base-components/pai-menu-item';
import PaiSelect from 'features/ui/base-components/pai-select';
import _ from 'lodash';
import React, { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { makeStyles } from 'tss-react/mui';
import PaiButton from '../../ui/base-components/pai-button';
import PaiTextField from '../../ui/base-components/pai-text-field';
import { LinkedinAccount } from '../linkedin-accounts/linkedin-accounts.types';

interface Props {
  open: boolean;
  loading: boolean;
  nameTakenError?: boolean;

  funnel?: FunnelOverview | null;
  funnelName: string;

  linkedinAccounts: LinkedinAccount[];
  selectedLinkedinAccount: string;

  handleFunnelNameChange(event: React.ChangeEvent<HTMLInputElement>): void;

  handleLinkedinAccountChange(event: React.ChangeEvent<HTMLInputElement>): void;

  handleCloneContactsChange(event: React.ChangeEvent<HTMLInputElement>): void;

  handleClose(): void;

  acceptFunnelName(): void;
}

const CloneCampaignDialog: React.FC<Props> = ({
  open,
  loading,
  nameTakenError,
  funnel,
  funnelName,
  linkedinAccounts = [],
  selectedLinkedinAccount,
  handleFunnelNameChange,
  handleLinkedinAccountChange,
  handleCloneContactsChange,
  handleClose,
  acceptFunnelName,
}) => {
  const { classes } = useStyles();
  const { t } = useTranslation();
  const [cloneContacts, setCloneContacts] = useState(false);

  const options = useMemo(() => linkedinAccounts.map((account) => ({ label: account.userEmail, value: account._id })), [linkedinAccounts]);

  const handleCloneContactsChangeLocal = (event: React.ChangeEvent<HTMLInputElement>) => {
    setCloneContacts(event.target.checked);
    handleCloneContactsChange(event);
  };

  return (
    <Dialog open={open} classes={{ paper: classes.paper }} fullWidth={true} maxWidth={'xs'}>
      <DialogTitle className={classes.title}>
        <Typography className={classes.titleText}>
          {_.isNil(funnel) ? t('createNewCampaign') : t('cloneExistingCampaign', { name: funnel.name })}
        </Typography>
      </DialogTitle>
      <DialogContent className={classes.content}>
        <Stack gap={2}>
          <div>
            <PaiTextField
              value={funnelName}
              variant={'background'}
              label={t('nameClonedCampaign')}
              placeholder={t('enterCampaignNameHere')}
              onChange={handleFunnelNameChange}
            />
            {nameTakenError ? <Typography className={classes.errorText}>{t('nameAlreadyTakenError')}</Typography> : null}
          </div>
          {!_.isEmpty(options) ? (
            <PaiSelect
              value={selectedLinkedinAccount}
              variant={'background'}
              label={t('selectLinkedinAccountForCampaign')}
              onChange={handleLinkedinAccountChange}
            >
              {options.map((option) => (
                <PaiMenuItem key={option.value} value={option.value}>
                  {option.label}
                </PaiMenuItem>
              ))}
            </PaiSelect>
          ) : null}
          <PaiCheckbox checked={cloneContacts} onChange={handleCloneContactsChangeLocal} label={t('cloneContactsWithCampaign')} />
        </Stack>
      </DialogContent>
      <DialogActions className={classes.actions}>
        <PaiLink text={t('cancel')} size={'sm'} underline={'always'} action={handleClose} />
        <PaiButton text={t('ok')} size={'small'} border={'rounded'} action={acceptFunnelName} disabled={funnelName.trim() === ''} loading={loading} />
      </DialogActions>
    </Dialog>
  );
};

const useStyles = makeStyles()((theme) => ({
  paper: {
    borderRadius: '16px',
  },
  title: {
    background: 'linear-gradient(to right, #5044FF, #4AA4FF)',
  },
  titleText: {
    fontSize: theme.typography.pxToRem(15),
    fontWeight: 'bold',
    textTransform: 'uppercase',
    color: '#FFF',
    textAlign: 'center',
  },
  content: {
    padding: '20px 24px !important',
    whiteSpace: 'pre-wrap',
  },
  text: {
    fontSize: theme.typography.pxToRem(15),
    whiteSpace: 'pre-wrap',
  },
  errorText: {
    fontSize: theme.typography.pxToRem(11),
    color: 'red',
    marginTop: '4px',
  },
  actions: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'flex-end',
    gap: '16px',
    padding: '0 24px 20px 24px',
  },
}));

export default CloneCampaignDialog;
